import type {
  BarometerSuccess,
} from '@autobid/strapi-integration/typescript/auction/barometerCar'
import type { CarFinishedEvent } from '@autobid/ui/types/websocket/CarFinishedEvent'
import { useWebSocketStore } from '@autobid/nuxt-pinia-store/store/useWebSocketStore'
import {
  getBarometerQueryKey,
} from '@autobid/ui/composables/car/data/useBarometerCar'
import { BAROMETER_SOCKET_CALLBACK } from '@autobid/ui/composables/car/sockets/useBarometerSocket'
import { useUpdateCar } from '@autobid/ui/composables/car/useUpdateCar'
import { BID_TYPES } from '@autobid/ui/constants/BID_TYPES'
import { randomDebounce } from '@autobid/ui/utils/randomDebounce'
import { useQueryClient } from '@tanstack/vue-query'

export function useBarometerCarFinished() {
  const { startListen } = useWebSocketStore()
  const queryClient = useQueryClient()
  const { updateBarometerCarData } = useUpdateCar()
  const { $trpc } = useNuxtApp()

  const carFinished = () => {
    const prepareNextCar = (event: CarFinishedEvent) => {
      const { nextHotbidStart, nextId } = event.context

      if (!nextId)
        return

      updateBarometerCarData(undefined, nextId, (data) => {
        return {
          ...data,
          hotbidStartTime: new Date(nextHotbidStart).toISOString(),
          stage: 'IN_AUCTION',
        }
      })
    }

    const invalidateCarInAuction = (auctionId: number) => {
      const queryKey = getBarometerQueryKey({
        type: 'current',
        auctionId,
      })

      /*
        setting the loadingNextCar flag is to avoid detecting wrong car in auction e.g. by function handleInvalidCarInAuction from the
        useBarometerCarEnterHotbid.ts file. It's all because we load/invalidate car in auction after 3-5 seconds. At this time,
        the courier is already sending the "premium.CAR_ENTER_HOTBID" event.
      */
      queryClient.setQueryData(
        queryKey,
        (
          data?: BarometerSuccess['data']['singleItem'],
        ): BarometerSuccess['data']['singleItem'] | undefined => {
          if (!data)
            return data

          return {
            ...data,
            item: {
              ...data.item,
              itemData: {
                ...data.item.itemData,
                loadingNextCar: true,
              },
            },
          }
        },
      )

      randomDebounce(3000, 5000, () => {
        queryClient.invalidateQueries(queryKey)
      })
    }

    const validateTheCarData = (event: CarFinishedEvent) => {
      const { carId, auctionId, price, buyerId } = event.context

      if (!price || !buyerId) {
        return true
      }

      const barometerQueryKey = getBarometerQueryKey({ type: 'item', carId })
      const currentBarometerQueryKey = getBarometerQueryKey({ type: 'current', auctionId })
      const barometerData = queryClient.getQueryData<BarometerSuccess['data']['singleItem']>(barometerQueryKey)
      const currentBarometerData = queryClient.getQueryData<BarometerSuccess['data']['singleItem']>(currentBarometerQueryKey)

      const items = [
        barometerData,
        currentBarometerData?.item.itemData.id === carId ? currentBarometerData : undefined,
      ].filter(el => el !== undefined)

      return items.every((data) => {
        const performLog = async () => {
          try {
            await $trpc.internal.createLog.mutate({
              level: 'error',
              internalCode: 'INVALID_CAR_DATA',
              carId,
              auctionId,
              state: { userId: data.item.itemData.bids[0]?.user_id, price: data.item.itemData.price.current },
              socket: { userId: buyerId, price },
            })
          }
          catch {
            // do nothing
          }
        }

        if (data.item.itemData.price.current !== price) {
          performLog()
          return false
        }

        const userIdOfLastBid = data.item.itemData.bids[0]?.user_id
        if (userIdOfLastBid && buyerId && userIdOfLastBid !== buyerId) {
          performLog()
          return false
        }

        return true
      })
    }

    const fixInvalidCarData = (event: CarFinishedEvent) => {
      const { carId, auctionId, price, buyerId, nickname } = event.context

      if (!price || !buyerId || !nickname) {
        return
      }

      updateBarometerCarData(auctionId, carId, (data) => {
        return {
          ...data,
          price: {
            ...data.price,
            current: price,
          },
          bids: [
            {
              ...data.bids[0],
              user_id: buyerId,
              user_nickname: nickname,
              amount: price,
              type: BID_TYPES.USER_BID,
              timestamp: new Date().toISOString().replace(/\.\d{3}Z$/, '+00:00'),
              status: 1,
              agent_bid: false,
            },
            ...data.bids,
          ],
        }
      })
    }

    startListen({
      eventName: 'premium.CAR_FINISHED',
      callbackName: BAROMETER_SOCKET_CALLBACK,
      callback: (event) => {
        /*
          courier sends double finish events, one of them is invalid and it will be fixed soon,
          the invalid event has input set to "premium"
        */
        if (event.input === 'premium')
          return

        const { carId, auctionId, itemStatus } = event.context

        updateBarometerCarData(auctionId, carId, (data) => {
          return {
            ...data,
            stage: 'FINISHED',
            inActiveHotbidPhase: false,
            state: itemStatus,
          }
        })
        prepareNextCar(event)
        invalidateCarInAuction(auctionId)
        !validateTheCarData(event) && fixInvalidCarData(event)
      },
    })
  }

  return { carFinished }
}
